<template>
  <b-form-group
    :id="`campaign-period-${periodIndex}-fieldset`"
    :label-for="`campaign-period-${periodIndex}-dropdown`"
    label-class="form-label"
    :data-test="`campaign-period-${periodIndex}`"
    class="mb-0"
  >
    <template
      v-if="periodIndex === 0"
      slot="label"
    >
      Campaign Period <span class="form-required">*</span>
    </template>
    <b-dropdown
      :id="`campaign-period-${periodIndex}-dropdown`"
      block
      lazy
      variant="outline-primary"
      :disabled="isDisabledPeriodDropdown"
    >
      <template v-slot:button-content>
        <p
          class="mb-0"
        >
          <template v-if="!startDateValue|| !endDateValue">
            choose campaign period
          </template>
          <template v-else>
            {{ formatDateForPlatform(startDateValue) }} - {{ formatDateForPlatform(endDateValue) }}
          </template>
        </p>
      </template>
      <b-dropdown-form
        style="max-width:790px;"
        :style="{ width: (windowWidth * 0.70) + 'px' }"
      >
        <b-row class="gutters-0">
          <b-col
            cols="6"
          >
            <b-form-group>
              <template
                slot="label"
              >
                Start Date <span class="form-required">*</span>
              </template>
              <datepicker
                :value="startDateValue"
                @selected="setCustomStartDate($event)"
                format="dd/MM/yyyy"
                :open-date="new Date(openStartDate)"
                :highlighted="highlightedDates"
                :disabled-dates="disabledDates"
                :bootstrap-styling="true"
                :monday-first="true"
                :inline="true"
                :data-test="`campaign-period-${periodIndex}-datepicker-start`"
                :disabled="isDisabledStartDatePicker"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
          >
            <b-form-group>
              <template
                slot="label"
              >
                End Date <span class="form-required">*</span>
              </template>
              <datepicker
                :value="endDateValue"
                @selected="setCustomEndDate($event)"
                format="dd/MM/yyyy"
                :open-date="new Date(openEndDate)"
                :highlighted="highlightedDates"
                :disabled-dates="disabledDates"
                :bootstrap-styling="true"
                :monday-first="true"
                :inline="true"
                :data-test="`campaign-period-${periodIndex}-datepicker-end`"
                :disabled="isDisabledEndDatePicker"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="gutters-0">
          <b-col cols="12">
            <b-button
              @click="clearDates"
              v-if="(startDateValue || endDateValue)"
              variant="secondary btn-sm"
              :data-test="`campaign-period-${periodIndex}-clear-dates`"
              :disabled="isClearDatesDisabled"
            >
              Clear Dates
            </b-button>
          </b-col>
        </b-row>
      </b-dropdown-form>
    </b-dropdown>
  </b-form-group>
</template>
<script>
import { mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

import dateHelper from '@/core/mixins/dateHelper';

import Datepicker from 'vuejs-datepicker';

import {
  startOfDay, endOfDay, parseISO, isPast,
} from 'date-fns';

export default {
  name: 'CampaignPeriod',
  mixins: [dateHelper],
  components: { Datepicker },
  props: {
    periods: {
      type: Array,
      required: true,
      default: () => [],
    },
    periodIndex: {
      type: Number,
      required: true,
      default: 0,
    },
    periodDateRange: {
      type: Object,
      required: true,
      default: () => ({
        startDate: null,
        endDate: null,
      }),
    },
    isPeriodRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
    hasPeriodEnded: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      today: new Date(),
      newDateRange: {
        startDate: null,
        endDate: null,
      },
    };
  },
  computed: {
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
      campaignTypes.GET_IS_CAMPAIGN_REVIEW,
      campaignTypes.GET_IS_CAMPAIGN_LIVE,
      campaignTypes.GET_IS_CAMPAIGN_PAUSED,
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_IS_READ_ONLY,
    ]),
    startDateValue() {
      return this.newDateRange.startDate ?? this.periodDateRange.startDate;
    },
    endDateValue() {
      return this.newDateRange.endDate ?? this.periodDateRange.endDate;
    },
    openStartDate() {
      if (this.startDateValue) return this.startDateValue;
      if (this.endDateValue) return this.endDateValue;
      if (this.periods.length
        && this.periodIndex > 0
        && !isPast(parseISO(this.periods[this.periodIndex - 1].endDate))) {
        return this.periods[this.periodIndex - 1].endDate;
      }
      return this.today;
    },
    openEndDate() {
      if (this.endDateValue) return this.endDateValue;
      if (this.startDateValue) return this.startDateValue;
      if (this.periods.length
        && this.periodIndex > 0
        && !isPast(parseISO(this.periods[this.periodIndex - 1].endDate))) {
        return this.periods[this.periodIndex - 1].endDate;
      }
      return this.today;
    },
    highlightedDates() {
      return {
        from: this.periodDateRange.startDate ? startOfDay(new Date(this.openStartDate)) : null,
        to: endOfDay(new Date(this.openEndDate)),
      };
    },
    disabledDates() {
      if (this.periods.length === 1) {
        return {
          to: this.today,
        };
      }
      if (this.periodIndex === 0) {
        return {
          customPredictor: (date) => date <= this.today
            || date >= new Date(this.periods[this.periodIndex + 1].startDate),
        };
      }
      if (this.periodIndex === this.periods.length - 1) {
        return {
          customPredictor: (date) => date <= this.today || date <= new Date(this.periods[this.periodIndex - 1].endDate),
        };
      }
      return {
        customPredictor: (date) => date <= this.today || date <= new Date(this.periods[this.periodIndex - 1].endDate)
          || date >= new Date(this.periods[this.periodIndex + 1].startDate),
      };
    },
    isDisabledPeriodDropdown() {
      if (this.GET_IS_CAMPAIGN_DRAFT) return false;
      return this.hasPeriodEnded
      || this.GET_IS_CAMPAIGN_READONLY
      || this.GET_IS_CAMPAIGN_REVIEW;
    },
    isDisabledStartDatePicker() {
      return this.hasPeriodEnded
      || this.isPeriodRunning
      || this.GET_IS_CAMPAIGN_REVIEW;
    },
    isDisabledEndDatePicker() {
      return this.hasPeriodEnded
      || this.GET_IS_CAMPAIGN_REVIEW;
    },
    isClearDatesDisabled() {
      return this.isPeriodRunning
      || this.GET_IS_CAMPAIGN_REVIEW;
    },
  },
  methods: {
    emitUpdatedDates() {
      this.$emit('updateDates', this.startDateValue, this.endDateValue);
    },
    setCustomStartDate(date) {
      this.newDateRange.startDate = date;
      if ((this.newDateRange.endDate || this.periodDateRange.endDate) && date > new Date(this.endDateValue)) this.newDateRange.endDate = date;
      this.emitUpdatedDates();
    },
    setCustomEndDate(date) {
      this.newDateRange.endDate = date;
      if ((this.newDateRange.startDate || this.periodDateRange.startDate) && date < new Date(this.newDateRange.startDate)) this.newDateRange.startDate = date;
      this.emitUpdatedDates();
    },
    clearDates() {
      if (this.isClearDatesDisabled) return;
      this.newDateRange.startDate = null;
      this.newDateRange.endDate = null;
      this.$emit('updateDates', null, null);
    },
  },
  created() {
    this.today.setHours(0, 0, 0, 0);
  },
};
</script>
